import { shopWallClockToEpoch } from '../../utils/shopTime';
import {
  Appointment,
  AppointmentTracking,
  CreateAppointmentDto,
  UpdateAppointmentDto,
  WorkshopFloor,
} from '../../types';
import api, { pagedResult, AppointmentListQuery, PagedResult } from './client';

// Appointments Service
export const appointmentsService = {
  createAppointment: async (appointment: CreateAppointmentDto): Promise<Appointment> => {
    const response = await api.post<Appointment>('/appointments', appointment);
    return response.data;
  },
  getAllAppointments: async (query?: AppointmentListQuery): Promise<Appointment[]> => {
    const response = await api.get<Appointment[]>('/appointments', { params: query });
    return response.data;
  },
  /** Same endpoint, but keeps the unpaged total for a paging control. */
  getAppointmentsPage: async (
    query: AppointmentListQuery,
  ): Promise<PagedResult<Appointment>> => {
    const response = await api.get<Appointment[]>('/appointments', { params: query });
    return pagedResult(response);
  },
  getAppointmentById: async (id: string): Promise<Appointment> => {
    const response = await api.get<Appointment>(`/appointments/${id}`);
    return response.data;
  },
  getAppointmentTracking: async (id: string): Promise<AppointmentTracking> => {
    const response = await api.get<AppointmentTracking>(`/appointments/track/${id}`);
    return response.data;
  },
  getMechanicAppointments: async (mechanicId: string): Promise<Appointment[]> => {
    const response = await api.get<Appointment[]>(`/appointments/mechanic/${mechanicId}`);
    return response.data;
  },
  getShopAppointments: async (
    shopId: string,
    query?: AppointmentListQuery,
  ): Promise<Appointment[]> => {
    const response = await api.get<Appointment[]>(`/appointments/shop/${shopId}`, {
      params: query,
    });
    return response.data;
  },
  /** The shop's bays and the jobs holding one, for the dashboard. */
  getWorkshopFloor: async (shopId: string): Promise<WorkshopFloor> => {
    const response = await api.get<WorkshopFloor>(`/appointments/shop/${shopId}/floor`);
    return response.data;
  },
  getAvailableTimeSlots: async (shopId: string, date: string): Promise<string[]> => {
    const response = await api.get<string[]>(`/appointments/available-slots?shopId=${shopId}&date=${date}`);
    return response.data;
  },
  /**
   * `timeZone` is the shop's, from its settings. Without it the instant is built
   * in the browser's zone, which quietly moves the appointment for any user not
   * sitting in the same zone as the shop.
   */
  rescheduleAppointment: async (
    id: string,
    date: string,
    time: string,
    timeZone?: string | null,
  ): Promise<Appointment> => {
    const appointmentDateTime = shopWallClockToEpoch(date, time, timeZone);

    const response = await api.patch<Appointment>(`/appointments/${id}/reschedule`, {
      appointmentDate: appointmentDateTime,
      appointmentTime: time,
      appointmentDateTime
    });
    return response.data;
  },
  updateAppointment: async (id: string, updateData: UpdateAppointmentDto): Promise<Appointment> => {
    const response = await api.patch<Appointment>(`/appointments/${id}`, updateData);
    return response.data;
  },
  /**
   * Customer self-service, authorized by the token in the emailed tracking
   * link — knowing the appointment id is enough to read the booking, not to
   * change it. Both return the refreshed tracking view.
   */
  rescheduleAsCustomer: async (
    id: string,
    token: string,
    appointmentDateTime: number,
  ): Promise<AppointmentTracking> => {
    const response = await api.patch<AppointmentTracking>(
      `/appointments/track/${id}/reschedule`,
      { token, appointmentDateTime },
    );
    return response.data;
  },
  cancelAsCustomer: async (
    id: string,
    token: string,
  ): Promise<AppointmentTracking> => {
    const response = await api.post<AppointmentTracking>(
      `/appointments/track/${id}/cancel`,
      { token },
    );
    return response.data;
  },
  markAppointmentCompleted: async (id: string): Promise<Appointment> => {
    const response = await api.patch<Appointment>(`/appointments/${id}/complete`, {});
    return response.data;
  },
  deleteAppointment: async (id: string): Promise<void> => {
    await api.delete(`/appointments/${id}`);
  },
};
